// unit-c-frontend/src/components/SocialMedia/MediaPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FiX } from 'react-icons/fi';
import mediaService from '../../services/mediaService';
import MediaUploadButton from './MediaUploadButton';
import MediaGrid from './MediaGrid';
import AudioList from './AudioList';
import { notify } from '../CustomToast';
import './MediaPanel.css';

const TABS = ['image', 'video', 'audio'];
const TAB_LABELS = { image: 'Image', video: 'Video', audio: 'Audio' };

const COLLAGE_HEIGHTS = [140, 100, 180, 120, 160, 110];

const MediaSkeleton = ({ tab }) => {
    if (tab === 'audio') {
        return (
            <div className="mp-skeleton-audio-list">
                {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="mp-skeleton-audio-card">
                        <div className="mp-skeleton mp-skeleton-audio-thumb" />
                        <div className="mp-skeleton-audio-info">
                            <div className="mp-skeleton mp-skeleton-audio-name" />
                            <div className="mp-skeleton mp-skeleton-audio-size" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    return (
        <div className="mp-skeleton-collage">
            {COLLAGE_HEIGHTS.map((h, i) => (
                <div key={i} className="mp-skeleton-block">
                    <div className="mp-skeleton" style={{ height: h }} />
                </div>
            ))}
        </div>
    );
};

const MediaPanel = ({ isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState('image');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchItems = useCallback(async (type) => {
        setLoading(true);
        try {
            const res = await mediaService.list(type);
            // Axios interceptor returns response.data directly
            // Response shape: { code, status, message, data: [...] }
            const list = res?.data ?? res;
            setItems(Array.isArray(list) ? list : []);
        } catch (err) {
            notify('error', err?.message || 'Failed to load media');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchItems(activeTab);
        }
    }, [isOpen, activeTab, fetchItems]);

    const handleUploaded = (newFile) => {
        setItems(prev => [newFile, ...prev]);
    };

    const handleDelete = async (id) => {
        try {
            await mediaService.remove(id);
            setItems(prev => prev.filter(f => f.id !== id));
        } catch (err) {
            notify('error', err?.message || 'Failed to delete media');
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setItems([]);
    };

    if (!isOpen) return null;

    return (
        <div className="mp-overlay">
            <div className="mp-drawer">
                <div className="mp-header">
                    <div className="mp-header-left">
                        <h2 className="mp-title">Media</h2>
                    </div>
                    <button className="mp-close-btn" onClick={onClose}>
                        <FiX size={20} />
                    </button>
                </div>

                <div className="mp-upload-area">
                    <MediaUploadButton
                        activeTab={activeTab}
                        onUploaded={handleUploaded}
                    />
                </div>

                <div className="mp-tabs">
                    {TABS.map(tab => (
                        <button
                            key={tab}
                            className={`mp-tab${activeTab === tab ? ' active' : ''}`}
                            onClick={() => handleTabChange(tab)}
                        >
                            {TAB_LABELS[tab]}
                        </button>
                    ))}
                </div>

                <div className="mp-body">
                    {loading ? (
                        <MediaSkeleton tab={activeTab} />
                    ) : activeTab === 'audio' ? (
                        <AudioList items={items} onDelete={handleDelete} />
                    ) : (
                        <MediaGrid items={items} onDelete={handleDelete} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default MediaPanel;
